import type { IMappedGeoMappingItem } from 'partner360/components/filters'

import { UNMAPPED_GEO_KEY, UNMAPPED_GEO_LABEL } from '../revenueAndSobHierarchy/constants'
import { buildGeoMappingTree } from '../revenueAndSobHierarchy/geoMappingHierarchy'
import { normalizeGeoKey } from '../revenueAndSobHierarchy/normalizeGeoKey'

import type { IAttentionGeoNode, IAttentionPartner } from './types'

interface IBuildAttentionGeoNodesParams {
  partners: IAttentionPartner[]
  geoMappingItems: IMappedGeoMappingItem[]
  /** First tree level to group by — `getAttentionGeoStartDepth` of the user's access level. */
  startDepth: number
}

/** A partner's ancestor chain, cut to the levels the drill-down shows. */
const getScopedChain = (
  countryCode: string,
  chainByCountry: ReturnType<typeof buildGeoMappingTree>['chainByCountry'],
  startDepth: number
): IMappedGeoMappingItem[] =>
  (chainByCountry.get(normalizeGeoKey(countryCode)) ?? []).slice(startDepth)

/**
 * Groups partners into the `GeoMapping` tree, keeping only the levels at or below `startDepth` and
 * only the branches the partners actually occupy. Partners whose country the tree does not cover
 * end up in one `Unmapped` node beside the real ones, so no partner disappears from the
 * drill-down. When `startDepth` is past the last level the tree has for every partner — a
 * country-level user, whose own level is the deepest one — there is no level left to group by and
 * the result is empty, which sends the drill-down straight to the partners.
 *
 * Every level the tree holds is a step of its own, including one that holds a single node: the
 * reader walks markets, then clusters, then countries, and a market with one country still reads
 * as that market until it is opened. A level that stands for one thing is still where that thing
 * lives, and skipping it would put two kinds of row — a place and a partner — on one list.
 */
export const buildAttentionGeoNodes = ({
  partners,
  geoMappingItems,
  startDepth,
}: IBuildAttentionGeoNodesParams): IAttentionGeoNode[] => {
  const { chainByCountry } = buildGeoMappingTree(geoMappingItems)

  const roots: IAttentionGeoNode[] = []
  const nodesByPath = new Map<string, IAttentionGeoNode>()
  let hasMappedPartner = false

  partners.forEach(({ partnerId, countryCode }) => {
    const chain = getScopedChain(countryCode, chainByCountry, startDepth)

    if (chain.length === 0) {
      const unmapped = nodesByPath.get(UNMAPPED_GEO_KEY)

      if (unmapped) {
        unmapped.partnerIds.push(partnerId)
        return
      }

      const node: IAttentionGeoNode = {
        itemId: UNMAPPED_GEO_KEY,
        name: UNMAPPED_GEO_LABEL,
        depth: startDepth,
        partnerIds: [partnerId],
        children: [],
      }
      nodesByPath.set(UNMAPPED_GEO_KEY, node)
      roots.push(node)
      return
    }

    hasMappedPartner = true

    let siblings = roots
    let path = ''

    chain.forEach((item, index) => {
      path = path ? `${path}/${item.itemId}` : item.itemId

      let node = nodesByPath.get(path)

      if (!node) {
        node = {
          itemId: item.itemId,
          name: item.name,
          depth: startDepth + index,
          partnerIds: [],
          children: [],
        }
        nodesByPath.set(path, node)
        siblings.push(node)
      }

      node.partnerIds.push(partnerId)
      siblings = node.children
    })
  })

  /* Nobody resolved to a node: the level does not exist for this group, rather than every partner
     being unmapped inside it. */
  return hasMappedPartner ? roots : []
}
